<template>
  <div class="mars-scheduler-grid">
    <grid
      :columnLines="true"
      :groupFeature="false"
      :searchFeature="false"
      :columnReorderFeature="false"
      :selectionMode="{ row: false, column: false }"
      :stripeFeature="true"
      :columns="columns"
      :data="items"
      @renderrows="renderRows"
    />
    <div class="tooltips-container">
      <b-tooltip
        v-for="(tooltip, index) in tooltips"
        :key="index"
        :target="tooltip.target"
        :show.sync="tooltip.showTooltip"
        triggers="click blur"
        customClass="mars-scheduler-tooltip"
        placement="bottom"
        @show="loadMarsRecord(tooltip.id)"
      >
        <div class="tooltip-header">
          <div>{{ tooltip.medication.name }}</div>
          <div class="flex">
            <img
              v-if="tooltip.type.toUpperCase() == 'PRN'"
              @click="deleteMedication(tooltip)"
              :src="imageDelete"
              class="b-action-item mr-2"
              style="width: 25px"
            />
            <img
              @click="editMedication(tooltip)"
              :src="imageEdit"
              class="b-action-item"
              style="width: 25px"
            />
          </div>
        </div>
        <div class="tooltip-body">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="badge badge-primary">{{
                tooltip.type.toUpperCase()
              }}</span>
            </div>
            <span :class="['text-white', 'badge', tooltip.badgeVariant]">
              {{ tooltip.status }}
            </span>
          </div>
          <div class="tooltip-item">
            <div><i class="b-fa b-fa-user"></i> Patient</div>
            <div class="tooltip-value px-2 m-1">
              {{
                formatEoc(
                  tooltip.episodeOfCare.patient.firstName,
                  tooltip.episodeOfCare.patient.middleName,
                  tooltip.episodeOfCare.patient.preferredName,
                  tooltip.episodeOfCare.patient.lastName,
                  tooltip.episodeOfCare.patient.mrn,
                  tooltip.episodeOfCare.eocn
                )
              }}
            </div>
          </div>
          <div class="tooltip-item">
            <div><i class="b-fa b-fa-clock"></i> Date Time</div>
            <div class="tooltip-value px-2 m-1">
              {{
                selectedLocationObj.marsMilitaryTimes
                  ? formatDateOnly(tooltip.dateTime) +
                    " " +
                    formatToMilitaryTime(tooltip.dateTime)
                  : formatDate(tooltip.dateTime)
              }}
            </div>
          </div>
          <div class="d-flex justify-content-between align-items-center">
            <div class="tooltip-item">
              <div>
                <i class="b-fa b-fa-prescription-bottle-alt"></i> Dosage
              </div>
              <div class="tooltip-value px-2 m-1">
                {{
                  tooltip.dosageAmount + " " + tooltip.unitOfMeasurement.name
                }}
              </div>
            </div>
            <div class="tooltip-item">
              <div><i class="b-fa b-fa-syringe"></i> Route</div>
              <div class="tooltip-value px-2 m-1">
                {{ tooltip.medicationRoute.name }}
              </div>
            </div>
          </div>
          <div v-if="tooltip.givenBy" class="tooltip-item">
            <div><i class="b-fa b-fa-user"></i> Given By</div>
            <div class="tooltip-value px-2 m-1">
              {{ tooltip.givenBy | formatUser }}
            </div>
          </div>
          <div v-if="tooltip.signedBy" class="tooltip-item">
            <div><i class="b-fa b-fa-user"></i> Signed By</div>
            <div class="tooltip-value px-2 m-1">
              {{ tooltip.signedBy | formatUser }}
            </div>
            <img
              style="width: 100%;"
              v-if="signatures[tooltip.id]"
              :src="signatures[tooltip.id]"
            />
          </div>
          <div v-if="tooltip.instructions" class="tooltip-item">
            <div><i class="b-fa b-fa-sticky-note"></i> Instructions</div>
            <div class="tooltip-value px-2 m-1">
              {{ tooltip.instructions }}
            </div>
          </div>
          <div v-if="tooltip.maximumDosagePerDay" class="tooltip-item">
            <div>
              <i class="b-fa b-fa-sticky-note"></i> Maximum dosage per day
            </div>
            <div class="tooltip-value px-2 m-1">
              {{ tooltip.maximumDosagePerDay }}
            </div>
          </div>
          <div v-if="tooltip.notes" class="tooltip-item">
            <div><i class="b-fa b-fa-sticky-note"></i> Notes</div>
            <div class="tooltip-value px-2 m-1">
              {{ tooltip.notes }}
            </div>
          </div>
        </div>
      </b-tooltip>
    </div>
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";
import {
  formatDateOnlyMysql,
  formatTimeOnly,
  formatToMilitaryTime
} from "../../helpers/date";
import moment from "moment";
import { mapState } from "vuex";
import marsServer from "../../server/marsRecord";

export default {
  name: "MarsSchedulerGrid",
  components: { Grid },
  props: [
    "list",
    "patientAvatars",
    "dateRange",
    "showAddEditMedication",
    "editMedicationId"
  ],
  data() {
    return {
      tooltips: [],
      formatToMilitaryTime: formatToMilitaryTime,
      signatures: {}
    };
  },
  methods: {
    editMedication(tooltip) {
      tooltip.showTooltip = false;
      this.$emit("update:editMedicationId", tooltip.id);
      this.$emit("update:showAddEditMedication", true);
    },
    deleteMedication(tooltip) {
      tooltip.showTooltip = false;
      this.$emit("deleteMedication", tooltip.id);
    },
    renderRows() {
      this.$nextTick(() => {
        this.setGridContentHeight();
        this.calculateTooltips();
      });
      this.$emit("renderRows");
    },
    setGridContentHeight() {
      const scroll = document.querySelector(
        ".mars-scheduler-grid .b-grid-vertical-scroller"
      );
      const content = document.querySelector(
        ".mars-scheduler-grid .b-grid-subgrid"
      );

      const scrollTop = scroll.getBoundingClientRect().top;
      const contentHeight = [...content.children].reduce((acc, child) => {
        return (acc += child.getBoundingClientRect().height);
      }, 0);

      scroll.style.height = `${contentHeight}px`;

      content.style.height = `${scrollTop + contentHeight}px`;
    },
    calculateTooltips() {
      this.tooltips = this.tooltips.concat(
        this.list.map(event => ({
          ...event,
          target: `e_${event.id}`,
          showTooltip: false,
          badgeVariant:
            event.status == "Pending"
              ? "badge-warning"
              : event.status == "Given"
              ? "badge-success"
              : "badge-danger"
        }))
      );
    },
    eventClass(event) {
      const statusClass =
        event.status == "Pending"
          ? "event-pending"
          : event.status == "Given"
          ? "event-given"
          : "event-not_given";

      return `event ${statusClass}`;
    },
    async loadMarsRecord(marsRecordId) {
      let marsRecord = await marsServer.get(marsRecordId, {
        "Hide-Loading": true
      });
      this.signatures = {
        ...this.signatures,
        [marsRecordId]: marsRecord.signature
      };
    }
  },
  computed: {
    ...mapState("layout", {
      selectedLocationObj: state => state.selectedLocationObj
    }),
    dates() {
      const dates = [];
      for (
        let dt = new Date(this.dateRange.start);
        dt <= this.dateRange.end;
        dt.setDate(dt.getDate() + 1)
      ) {
        dates.push(new Date(dt));
      }

      return dates;
    },
    items() {
      const items = Object.values(
        this.list.reduce((acc, item) => {
          item.rowHeight = 200;
          const eocId = item.episodeOfCare.id;
          const key = formatDateOnlyMysql(item.dateTime);
          acc[eocId] = { ...(acc[eocId] ?? {}) };
          acc[eocId]["episodeOfCare"] = item.episodeOfCare;
          acc[eocId]["entries"] = acc[eocId]["entries"] ?? [];
          acc[eocId]["entries"][key] = [
            ...(acc[eocId]["entries"][key] ?? []),
            item
          ];
          return acc;
        }, {})
      );
      items.forEach(item => {
        let max = -Infinity;
        Object.values(item.entries).forEach(function(a) {
          if (a.length > max) {
            max = a.length;
          }
        });
        item.rowHeight = max * 46 + 20;
      });

      items.sort(
        (a, b) =>
          a.episodeOfCare.patient.firstName +
          a.episodeOfCare.patient.middleName +
          a.episodeOfCare.patient.lastName.localeCompare(
            b.episodeOfCare.patient.firstName +
              b.episodeOfCare.patient.middleName +
              b.episodeOfCare.patient.lastName
          )
      );
      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        for (
          let index2 = 0;
          index2 < Object.keys(item.entries).length;
          index2++
        ) {
          const entry = Object.keys(item.entries)[index2];
          item.entries[entry].sort((a, b) =>
            a.dateTime.localeCompare(b.dateTime)
          );
        }
      }
      return items;
    },
    columns() {
      return [
        {
          text: "name",
          type: "template",
          width: 250,
          editor: false,
          sortable: false,
          template: ({ record }) => {
            const eoc = record.data.episodeOfCare;
            const patient = this.formatEocObj(eoc);
            const src = eoc.patient.avatarId
              ? this.patientAvatars[eoc.patient.avatarId]
              : require(`../../assets/avatar-example.png`);
            return `<img class="avatar-box" src="${src}"/> ${patient}`;
          }
        },
        {
          text: this.dateRangeLabel,
          align: "center",
          flex: 1,
          children: this.dates.map(date => ({
            text: moment(date).format("D MMM"),
            type: "template",
            align: "center",
            flex: 1,
            sortable: false,
            editor: false,
            template: ({ record }) => {
              const key = formatDateOnlyMysql(date);
              const entries = record.originalData.entries[key] || [];
              return entries.reduce((acc, entry) => {
                const entryClass = this.eventClass(entry);
                let gridtime;
                if (this.selectedLocationObj.marsMilitaryTimes) {
                  gridtime = formatToMilitaryTime(entry.dateTime);
                } else {
                  gridtime = formatTimeOnly(entry.dateTime);
                }
                acc += `
                  <a class="${entryClass}" tabindex="0" id="e_${entry.id}">
                    <div class="d-flex flex-column text-left">
                      <span>${entry.medication.name}</span>
                      <span>${gridtime}</span> 
                    </div>
                  </div>`;
                return acc;
              }, "");
            }
          }))
        }
      ];
    },
    dateRangeLabel() {
      const startDate = this.dateRange.start;
      const endDate = this.dateRange.end;
      let displayYear = false;
      let displayMonth = false;

      if (startDate.getFullYear() != endDate.getFullYear()) {
        displayYear = true;
        displayMonth = true;
      } else if (startDate.getMonth() != endDate.getMonth()) {
        displayMonth = true;
      }

      let label = `${moment(startDate).format("YYYY MMM DD")} - `;
      label += displayYear ? `${endDate.getFullYear()} ` : "";
      label += displayMonth ? `${moment(endDate).format("MMM")} ` : "";
      label += moment(endDate).format("DD");

      return label;
    },
    imageEdit() {
      return this.isStandalone()
        ? "/icons/edit.svg"
        : "/desktop/icons/edit.svg";
    },
    imageDelete() {
      return this.isStandalone()
        ? "/icons/delete.svg"
        : "/desktop/icons/delete.svg";
    }
  },
  watch: {
    list() {
      this.tooltips.splice(0);
    }
  }
};
</script>

<style lang="scss">
.mars-scheduler-grid {
  display: flex;
  flex: 1;

  .b-grid-container {
    display: flex;
    width: 100%;

    .b-grid {
      width: 100%;
      max-height: 100%;
    }
  }

  .avatar-box {
    border-radius: 35px;
    width: 40px;
    height: 40px;
    margin-right: 5px;
  }

  .b-grid-header,
  .b-grid-header-text {
    padding-top: 0;
    padding-bottom: 0;
  }
  .b-grid-header-text {
    height: 24px;
  }

  .b-grid-body-container {
    height: 100% !important;
  }

  .b-grid-subgrid {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    pointer-events: none;
    min-height: 100%;
  }

  .b-grid-row {
    pointer-events: auto;
    border-bottom: 2px solid #e9eaeb !important;
  }

  .b-grid-row .b-grid-cell:not(:first-child) {
    flex-direction: column;
    justify-content: flex-start;
    padding: 1px 0;

    .b-action-ct {
      width: 100%;
    }
  }

  .event {
    display: block;
    width: 100%;
    height: 40px;
    color: white;
    font-size: 13px;
    margin: 3px 0;
    padding: 0 0.5em 0 0.75em;
    cursor: pointer;
    white-space: nowrap;

    &.event-pending {
      background-color: #ffc300;
    }

    &.event-given {
      background-color: #66c854;
    }

    &.event-not_given {
      background-color: #ff6161;
    }

    &:hover {
      text-decoration: none;
    }
  }
}

.mars-scheduler-tooltip.tooltip.b-tooltip.bs-tooltip-top .arrow:before,
.mars-scheduler-tooltip.tooltip.b-tooltip.bs-tooltip-bottom .arrow:before {
  border-top-color: white;
  border-bottom-color: white;
}
.mars-scheduler-tooltip.tooltip.b-tooltip.bs-tooltip-left .arrow:before {
  border-left-color: white;
}
.mars-scheduler-tooltip.tooltip.b-tooltip.bs-tooltip-right .arrow:before {
  border-right-color: white;
}

.mars-scheduler-tooltip.tooltip.b-tooltip {
  opacity: 1;

  .tooltip-inner {
    max-width: inherit;
    min-width: 20rem;
    min-height: 10rem;
    max-height: 40rem;
    background-color: white;
    color: #4f5964;
    text-align: left;
    font-size: 16px;
    padding: 0;
    border-radius: 5px 5px 0 0;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12), 0 1px 6px rgba(0, 0, 0, 0.24);
  }

  .b-tooltip-content {
    color: #4f5964;
  }

  .tooltip-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f6f6f6;
    height: 40px;
    padding: 15px;
    border-radius: 5px 5px 0 0;
  }

  .tooltip-body {
    margin: 5px 10px;
    max-width: 300px;
    max-height: 500px;
    overflow-y: auto;
  }
}
</style>
